/**
 * A row returned by the product search (`/orders/:storeId/products/query`), which hands back raw
 * product rows rather than a projection.
 */
export interface ProductSourceIdRow {
  productId?: string | null;
  variantId?: string | null;
  parent?: { productId?: string | null } | null;
}

export interface ProductSourceIds {
  sourceProductId: string | null;
  sourceVariantId: string | null;
}

/**
 * Resolves the (product, variant) pair to record on a claim item.
 *
 * Two row shapes reach this while the ENG-5825 backfill rolls out store by store, and they disagree
 * about what `productId` means:
 *
 *   migrated — productId is the product and variantId the variant;
 *   legacy   — productId holds the *variant* id, and only the parent knows the product id.
 *
 * Deriving either id without checking which shape it is gets one of them wrong: reading the variant
 * out of `productId` makes both ids identical on a migrated row, while trusting `productId` as the
 * product id yields a variant id on a legacy one.
 *
 * When neither a variantId nor a parent is present there is nothing to prove `productId` is a
 * variant, so the variant is left null rather than guessed. Copying the product id across would
 * stamp a product id into source_variant_id, which downstream reads treat as a variant and fail to
 * match — and callers that need a lookup key already fall back to sourceProductId themselves.
 */
export function resolveProductSourceIds(row: ProductSourceIdRow | null | undefined): ProductSourceIds {
  if (!row) return { sourceProductId: null, sourceVariantId: null };

  // Migrated: the row carries both ids itself.
  if (row.variantId) {
    return { sourceProductId: row.productId ?? null, sourceVariantId: row.variantId };
  }

  // Legacy: a parent is what proves productId is holding the variant id.
  if (row.parent?.productId) {
    return { sourceProductId: row.parent.productId, sourceVariantId: row.productId ?? null };
  }

  return { sourceProductId: row.productId ?? null, sourceVariantId: null };
}
